// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  TextInput,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useIsFocused } from '@react-navigation/native';
import { usePathname, useRouter } from 'expo-router';
import { SvgXml } from 'react-native-svg';
import * as WebBrowser from 'expo-web-browser';
import { Text } from '@/components/Themed';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import { createRequestId } from '@/services/requestId';
import DeleteAccountModal from '@/components/DeleteAccountModal';
import ApiAccessSection from '@/components/ApiAccessSection';
import GoffyButton from '@/components/GoffyButton';
import GoffyInput from '@/components/GoffyInput';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import { CoachMarkCallout } from '@/components/CoachMarkCallout';
import { buildLogoutUrl, POST_LOGOUT_REDIRECT, revokeRefreshToken } from '@/auth/oidc';
import { useUser } from '@/app/user-context';
import { useChangePassword } from '@/hooks/useChangePassword';
import { useDeleteAccount } from '@/hooks/useDeleteAccount';
import { useI18n } from '@/i18n';
import {
  buildProfileUpdate,
  golfIdMaxLength,
  normalizeHomeClubs,
  parseSearchVisibility,
  searchVisibilityOptions,
  serializeProfileUpdate,
  type HomeClubProfile,
  type ProfileFormFields,
  type ProfileUpdatePayload,
  type SearchVisibility,
} from '@/features/profile/autosave';
import { parseDistanceUnit, type DistanceUnit } from '@/features/units/distance';
import { useDistanceUnit, useDistanceUnitState } from '@/stores/distanceUnitState';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import SkinPicker from '@/components/SkinPicker';
import { useShotTrackingState } from '@/stores/shotTrackingState';
import { useHazardAnimationState } from '@/stores/hazardAnimationState';
import { useShowDistanceState } from '@/stores/showDistanceState';
import { useRecentCourses } from '@/hooks/useRecentCourses';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAvatarSvg } from '@/services/avatarSvg';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import {
  profileImageActionsCoachMarkKey,
  useCoachMark,
  useCoachMarkPreferences,
} from '@/services/coachMarks';
import { ensureCurrentPlayer } from '@/services/ensureCurrentPlayer';
import { formatLocalDate, localDateToDate } from '@/services/localDate';
import appColors, { alpha } from '@/theme/colors';

type PlayerProfile = {
  id: string;
  name?: string;
  displayName?: string | null;
  imageKey?: string | null;
  imageThumbKey?: string | null;
  gender?: string | null;
  handicap?: number | null;
  handicapDate?: string | null;
  Gender?: string | null;
  Handicap?: number | null;
  HandicapDate?: string | null;
};

type UserProfile = {
  id: string;
  username?: string;
  displayName?: string | null;
  fullName?: string | null;
  email?: string | null;
  createdAt?: string;
  golfId?: string | null;
  searchVisibility?: string | null;
  enhancedInput?: boolean | null;
  distanceUnit?: string | null;
  homeClubs?: HomeClubProfile[] | null;
};

const searchVisibilityLabelKeys: Record<SearchVisibility, string> = {
  name: 'profile.searchVisibilityName',
  golfId: 'profile.searchVisibilityGolfId',
  never: 'profile.searchVisibilityNever',
};

const searchVisibilityHelpKeys: Record<SearchVisibility, string> = {
  name: 'profile.searchVisibilityNameHelp',
  golfId: 'profile.searchVisibilityGolfIdHelp',
  never: 'profile.searchVisibilityNeverHelp',
};

type ClubOption = {
  id: string;
  name: string;
  city?: string;
  country?: string;
  /** Every course the club owns that the caller may see. The ids mark a club as played (a round
   *  records a course); the names are what the picker row shows. */
  courses?: Array<{ id: string; name: string }>;
};

type PendingUpload = {
  blob: Blob;
  contentType: string;
  fileName: string;
  fileSize: number;
};

export default function ProfileScreen() {
  const { user, setUser } = useUser();
  const { t, locale: activeLocale } = useI18n();
  const isFocused = useIsFocused();
  const pathname = usePathname();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const imageActionsAnchorRef = useRef<View>(null);
  const [imageCoachMarkDismissed, setImageCoachMarkDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  /**
   * Only used to order the club picker, so it asks for the most tour-service will give: the
   * endpoint clamps limit to 10, which is where "clubs you have played" stops for now.
   */
  const recentCourses = useRecentCourses(player?.id, user?.token, 10);
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [golfId, setGolfId] = useState('');
  const [searchVisibility, setSearchVisibility] = useState<SearchVisibility>('name');
  const [enhancedInput, setEnhancedInput] = useState(false);
  const [enhancedInputSaving, setEnhancedInputSaving] = useState(false);
  // Held in the shared store rather than local state: every screen that draws a distance
  // reads the same value, so it has to change everywhere the moment it is picked.
  const distanceUnit = useDistanceUnit();
  const shotTrackingEnabled = useShotTrackingState((state) => state.enabled);
  const setShotTrackingEnabled = useShotTrackingState((state) => state.setEnabled);
  const showDistanceEnabled = useShowDistanceState((state) => state.enabled);
  const setShowDistanceEnabled = useShowDistanceState((state) => state.setEnabled);
  const hazardAnimationsEnabled = useHazardAnimationState((state) => state.enabled);
  const setHazardAnimationsEnabled = useHazardAnimationState((state) => state.setEnabled);
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const setDistanceUnit = useDistanceUnitState((state) => state.setUnit);
  const [distanceUnitSaving, setDistanceUnitSaving] = useState(false);
  const [homeClubs, setHomeClubs] = useState<HomeClubProfile[]>([]);
  const [clubOptions, setClubOptions] = useState<ClubOption[]>([]);
  const [loadingClubOptions, setLoadingClubOptions] = useState(false);
  const [clubPickerOpen, setClubPickerOpen] = useState(false);
  const [clubSearch, setClubSearch] = useState('');
  const [gender, setGender] = useState('');
  const [handicap, setHandicap] = useState('');
  const [handicapDate, setHandicapDate] = useState('');
  const [handicapDatePickerOpen, setHandicapDatePickerOpen] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [avatarChoices, setAvatarChoices] = useState<Array<{ id: string; seed: string }>>([]);
  const [avatarRemovedLocally, setAvatarRemovedLocally] = useState(false);
  const avatarRefreshLock = useRef(false);
  const pendingUploadRef = useRef<PendingUpload | null>(null);
  const imageRequestIdRef = useRef<string | null>(null);
  const updateRequestIdRef = useRef<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** Serialized payload of the last update sent, and the only thing that keeps repeat commits quiet. */
  const lastSentPayloadRef = useRef<string | null>(null);
  /** Set once the loaded profile has been folded into `lastSentPayloadRef`, so a failed save --
   *  which clears that ref to allow a retry -- cannot be mistaken for "not seeded yet". */
  const baselineSeededRef = useRef(false);
  /** i18n key of the rule holding the save back, e.g. a handicap typed before its date. */
  const [holdReason, setHoldReason] = useState<string | null>(null);
  const [autosave, setAutosave] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [apiAccessDialogOpen, setApiAccessDialogOpen] = useState(false);
  // The coach mark renders its own full-screen Modal; react-native-web aria-hides
  // sibling modals beneath the most recently mounted one, so keep it off while any
  // dialog on this screen is open.
  const anyDialogOpen =
    passwordModalOpen ||
    deleteAccountModalOpen ||
    apiAccessDialogOpen ||
    avatarPickerOpen ||
    clubPickerOpen ||
    handicapDatePickerOpen;

  const clearSaveTimeout = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, []);

  /**
   * A profile update only fails via a player.update.error reply or this timeout — never via the
   * send() return value: 'queued' means the frame is flushed when the socket (re)opens and the
   * update is still in flight.
   */
  const armSaveTimeout = useCallback(
    (requestId: string) => {
      clearSaveTimeout();
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        if (updateRequestIdRef.current !== requestId) {
          return;
        }
        updateRequestIdRef.current = null;
        lastSentPayloadRef.current = null;
        setSaving(false);
        setAutosave('failed');
        setStatusNote(null);
        setError(t('profile.saveFailed'));
      }, 12000);
    },
    [clearSaveTimeout, t]
  );

  useEffect(() => clearSaveTimeout, [clearSaveTimeout]);
  const {
    visible: showImageCoachMark,
    dismissForever: dismissImageCoachMarkForever,
    disableAll: disableAllCoachMarks,
  } = useCoachMark(
    apiBaseUrl,
    profileImageActionsCoachMarkKey,
    !imageCoachMarkDismissed && isFocused && !anyDialogOpen
  );

  const {
    enabled: coachTipsEnabled,
    loading: coachTipsLoading,
    saving: coachTipsSaving,
    hasDismissedTips,
    setEnabled: setCoachTipsEnabled,
    resetAll: resetCoachTips,
  } = useCoachMarkPreferences(apiBaseUrl);
  const [coachTipsError, setCoachTipsError] = useState<string | null>(null);

  const {
    changePassword,
    loading: passwordSaving,
    error: passwordError,
    reset: resetPasswordState,
  } = useChangePassword({
    apiBaseUrl,
    token: user?.token,
    onSuccess: () => {
      setPasswordModalOpen(false);
      setHasPassword(true);
      setStatusNote(t('profile.passwordChanged'));
    },
  });

  /**
   * The account is already gone server-side by the time this runs, so every step is best-effort:
   * clear the local session first, then leave the screen, then try to tidy up the Keycloak side.
   */
  const finishAccountDeletion = useCallback(async () => {
    const idTokenHint = user?.idToken ?? user?.token ?? null;
    const refreshToken = user?.refreshToken ?? null;
    setDeleteAccountModalOpen(false);
    setUser(null);
    router.replace('/');
    if (refreshToken) {
      try {
        await revokeRefreshToken(refreshToken);
      } catch {
        // Deleting the Keycloak user already invalidated the token; nothing to recover here.
      }
    }
    const logoutUrl = buildLogoutUrl(idTokenHint);
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        window.location.href = logoutUrl;
      } else {
        await WebBrowser.openAuthSessionAsync(logoutUrl, POST_LOGOUT_REDIRECT);
      }
    } catch {
      // The local session is cleared either way, so a failed SSO round trip is not worth surfacing.
    }
  }, [router, setUser, user?.idToken, user?.refreshToken, user?.token]);

  const {
    deleteAccount,
    loading: deletingAccount,
    error: deleteAccountError,
    reset: resetDeleteAccountState,
  } = useDeleteAccount({
    apiBaseUrl,
    userId: user?.id,
    token: user?.token,
    onDeleted: finishAccountDeletion,
  });

  const autosaveLabel = useMemo(() => {
    switch (autosave) {
      case 'saving':
        return t('profile.saving');
      case 'saved':
        return t('profile.autosaveSaved');
      case 'failed':
        return t('profile.autosaveFailed');
      default:
        return null;
    }
  }, [autosave, t]);

  const resolvedImage = avatarRemovedLocally ? null : player?.imageThumbKey || player?.imageKey || null;
  const genderOptions = useMemo(
    () => [
      { value: 'male', label: t('profile.genderMale') },
      { value: 'female', label: t('profile.genderFemale') },
      { value: 'neutral', label: t('profile.genderNeutral') },
    ],
    [t]
  );
  const normalizedHomeClubs = useCallback(
    (items: HomeClubProfile[]) =>
      items
        .map((item, index) => ({
          clubId: item.clubId,
          clubName: item.clubName?.trim() ?? '',
          clubCity: item.clubCity?.trim() || null,
          isPrimary: item.isPrimary === true,
          _order: index,
        }))
        .sort((a, b) => {
          if (a.isPrimary === b.isPrimary) {
            if (a.clubId === b.clubId) {
              return a._order - b._order;
            }
            return a.clubId.localeCompare(b.clubId);
          }
          return a.isPrimary ? -1 : 1;
        })
        .map(({ _order, ...rest }) => rest),
    []
  );
  /**
   * Courses the player has actually played, as club ids. Rounds record a course rather than a club,
   * so the club list's own `courses` is what turns one into the other.
   */
  const playedClubIds = useMemo(() => {
    const playedCourseIds = new Set(recentCourses.map((course) => course.id));
    if (playedCourseIds.size === 0) {
      return new Set<string>();
    }
    return new Set(
      clubOptions
        .filter((club) => (club.courses ?? []).some((course) => playedCourseIds.has(course.id)))
        .map((club) => club.id)
    );
  }, [clubOptions, recentCourses]);

  const filteredClubOptions = useMemo(() => {
    const query = clubSearch.trim().toLowerCase();
    const selectedIds = new Set(homeClubs.map((club) => club.clubId));
    let available = clubOptions.filter((club) => !selectedIds.has(club.id));
    if (query) {
      available = available.filter((club) => {
        const name = club.name.toLowerCase();
        const city = (club.city ?? '').toLowerCase();
        const country = (club.country ?? '').toLowerCase();
        // Course names too: people know the course they play more often than its club's name.
        const courses = (club.courses ?? []).map((course) => course.name.toLowerCase());
        return (
          name.includes(query) ||
          city.includes(query) ||
          country.includes(query) ||
          courses.some((courseName) => courseName.includes(query))
        );
      });
    }
    // Clubs the player has played come first, each group alphabetical. localeCompare, not a plain
    // sort: å/ä/ö belong at the end of a Swedish list, not next to a.
    return [...available].sort((a, b) => {
      const aPlayed = playedClubIds.has(a.id);
      const bPlayed = playedClubIds.has(b.id);
      if (aPlayed !== bPlayed) {
        return aPlayed ? -1 : 1;
      }
      return a.name.localeCompare(b.name, activeLocale);
    });
  }, [activeLocale, clubOptions, clubSearch, homeClubs, playedClubIds]);

  const resetUserFields = useCallback(() => {
    setFullName('');
    setEmail('');
    setGolfId('');
    setSearchVisibility('name');
    setEnhancedInput(false);
    setHomeClubs([]);
  }, []);

  const loadPlayer = useCallback(async () => {
    if (!user?.token || !user?.id) return;
    setLoading(true);
    setError(null);
    // Whatever comes back is the new baseline; the seeding effect below re-arms once it lands.
    baselineSeededRef.current = false;
    lastSentPayloadRef.current = null;
    setHoldReason(null);
    setAutosave('idle');
    try {
      const ensured = await ensureCurrentPlayer(apiBaseUrl, user.token, user.id, {
        timeoutMs: 12_000,
        intervalMs: 500,
      });

      if (ensured.status === 'timeout' || ensured.status === 'notFound') {
        setStatusNote(t('profile.playerProvisioning'));
        setPlayer(null);
        return;
      }

      if (ensured.status === 'unauthorized') {
        throw new Error('profile.load.failed.unauthorized');
      }

      if (ensured.status === 'error') {
        throw new Error(ensured.message);
      }

      const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (response.status === 404) {
        setStatusNote(t('profile.playerProvisioning'));
        setPlayer(null);
        return;
      }
      if (!response.ok) {
        throw new Error('profile.load.failed');
      }
      const payload = (await response.json()) as PlayerProfile;
      setStatusNote(null);
      setPlayer(payload);
      setDisplayName(payload.displayName ?? payload.name ?? '');
      try {
        const userResponse = await fetch(`${apiBaseUrl}/api/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (userResponse.ok) {
          const userPayload = (await userResponse.json()) as UserProfile;
          const loadedFullName = (userPayload.fullName ?? userPayload.displayName ?? '').trim();
          setFullName(loadedFullName);
          setEmail((userPayload.email ?? '').trim());
          setGolfId((userPayload.golfId ?? '').trim());
          setSearchVisibility(parseSearchVisibility(userPayload.searchVisibility));
          setEnhancedInput(userPayload.enhancedInput === true);
          // The profile is the source of truth; this overwrites the locally cached mirror.
          setDistanceUnit(parseDistanceUnit(userPayload.distanceUnit));
          const loadedHomeClubs = (userPayload.homeClubs ?? []).map((club) => ({
            clubId: club.clubId,
            clubName: club.clubName,
            clubCity: club.clubCity ?? null,
            isPrimary: club.isPrimary === true,
          }));
          setHomeClubs(normalizeHomeClubs(loadedHomeClubs));
        } else {
          resetUserFields();
        }
      } catch {
        resetUserFields();
      }
      try {
        const passwordResponse = await fetch(`${apiBaseUrl}/api/users/${user.id}/password`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (passwordResponse.ok) {
          const passwordPayload = (await passwordResponse.json()) as { hasPassword?: boolean };
          setHasPassword(passwordPayload.hasPassword === true);
        } else {
          setHasPassword(null);
        }
      } catch {
        setHasPassword(null);
      }
      const nextGender = payload.gender ?? payload.Gender ?? '';
      const nextHandicap = payload.handicap ?? payload.Handicap;
      const nextHandicapDate = payload.handicapDate ?? payload.HandicapDate ?? '';
      setGender(nextGender);
      setHandicap(nextHandicap === null || nextHandicap === undefined ? '' : String(nextHandicap));
      setHandicapDate(nextHandicapDate ? String(nextHandicapDate).slice(0, 10) : '');
    } catch {
      setError(t('profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, resetUserFields, setDistanceUnit, t, user?.id, user?.token]);

  useEffect(() => {
    loadPlayer();
  }, [loadPlayer]);

  const loadClubOptions = useCallback(async () => {
    if (!user?.token) return;
    setLoadingClubOptions(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/clubs`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error('profile.homeClubsLoadFailed');
      }

      const payload = (await response.json()) as ClubOption[];
      setClubOptions(payload ?? []);
    } catch {
      setError(t('profile.homeClubsLoadFailed'));
    } finally {
      setLoadingClubOptions(false);
    }
  }, [apiBaseUrl, t, user?.token]);

  useEffect(() => {
    if (!isFocused) return;
    void loadClubOptions();
  }, [isFocused, loadClubOptions]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'player.image.presign.ack') {
        const payload = message as { requestId?: string; uploadUrl?: string; publicUrl?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        const pending = pendingUploadRef.current;
        if (!pending || !payload.uploadUrl || !payload.publicUrl) {
          imageRequestIdRef.current = null;
          pendingUploadRef.current = null;
          setUploading(false);
          setError(t('profile.uploadFailed'));
          return;
        }
        (async () => {
          try {
            const uploadResp = await fetch(payload.uploadUrl!, {
              method: 'PUT',
              headers: {
                'Content-Type': pending.contentType,
                'Cache-Control': 'public, max-age=31536000, immutable',
              },
              body: pending.blob,
            });
            if (!uploadResp.ok) {
              throw new Error(`Upload failed ${uploadResp.status}`);
            }
            const updateRequestId = generateId();
            updateRequestIdRef.current = updateRequestId;
            socketRef.current.send({
              type: 'player.profile.update',
              playerId: player?.id,
              requestId: updateRequestId,
              imageKey: payload.publicUrl,
              removeAvatar: false,
            });
            setAvatarRemovedLocally(false);
            setPlayer((prev) => (prev ? { ...prev, imageKey: payload.publicUrl, imageThumbKey: payload.publicUrl } : prev));
            setStatusNote(t('profile.uploadSuccess'));
          } catch {
            setError(t('profile.uploadFailed'));
          } finally {
            setUploading(false);
            imageRequestIdRef.current = null;
            pendingUploadRef.current = null;
          }
        })();
        return;
      }
      if (type === 'player.image.presign.error') {
        const payload = message as { requestId?: string; message?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        imageRequestIdRef.current = null;
        pendingUploadRef.current = null;
        setUploading(false);
        setError(payload.message ?? t('profile.uploadFailed'));
        return;
      }
      if (type === 'player.update.ack') {
        const payload = message as { requestId?: string };
        console.info('[profile] player.update.ack received', {
          expectedRequestId: updateRequestIdRef.current,
          receivedRequestId: payload.requestId,
        });
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          console.info('[profile] player.update.ack ignored due to requestId mismatch');
          return;
        }
        updateRequestIdRef.current = null;
        clearSaveTimeout();
        setSaving(false);
        setAutosave('saved');
        setStatusNote(t('profile.saveSuccess'));
        return;
      }
      if (type === 'player.update.error') {
        const payload = message as { requestId?: string; message?: string; code?: string };
        console.warn('[profile] player.update.error received', {
          expectedRequestId: updateRequestIdRef.current,
          receivedRequestId: payload.requestId,
          message: payload.message,
        });
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          console.warn('[profile] player.update.error ignored due to requestId mismatch');
          return;
        }
        updateRequestIdRef.current = null;
        clearSaveTimeout();
        lastSentPayloadRef.current = null;
        setAvatarRemovedLocally(false);
        setSaving(false);
        setAutosave('failed');
        setError(translateUpdateError(payload.code ?? payload.message, t));
        return;
      }
      if (type === 'players_snapshot') {
        const payload = message as { players?: PlayerProfile[]; Players?: PlayerProfile[] };
        const list = payload.players ?? payload.Players ?? [];
        const updated = list.find((p) => p.id === player?.id);
        if (updated) {
          setPlayer((prev) =>
            avatarRemovedLocally
              ? { ...(prev ?? updated), ...updated, imageKey: null, imageThumbKey: null }
              : { ...(prev ?? updated), ...updated }
          );
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [avatarRemovedLocally, clearSaveTimeout, player?.id, t, user?.token]);

  const formFields: ProfileFormFields = {
    displayName,
    email,
    gender,
    handicap,
    handicapDate,
    golfId,
    searchVisibility,
    homeClubs,
  };
  /** Lets the commit run from an unmount cleanup, where the render's own values are long gone. */
  const formFieldsRef = useRef(formFields);
  useEffect(() => {
    formFieldsRef.current = formFields;
  });

  const sendProfileUpdate = useCallback(
    (update: ProfileUpdatePayload) => {
      if (!player?.id) return;
      setSaving(true);
      setAutosave('saving');
      setError(null);
      setStatusNote(t('profile.saving'));
      const requestId = generateId();
      updateRequestIdRef.current = requestId;
      socketRef.current.setAuthToken(user?.token ?? null);
      const payload = {
        type: 'player.profile.update',
        playerId: player.id,
        requestId,
        ...update,
      };
      console.info('[profile] sending player.profile.update', {
        requestId,
        playerId: player.id,
        hasEmail: payload.email !== undefined,
        hasHomeClubs: Array.isArray(payload.homeClubs),
        homeClubCount: payload.homeClubs.length,
      });
      const sent = socketRef.current.send(payload);
      if (sent !== 'sent') {
        console.info('[profile] player.profile.update queued until the socket reopens', {
          requestId,
          playerId: player.id,
        });
      }
      armSaveTimeout(requestId);
    },
    [armSaveTimeout, player?.id, t, user?.token]
  );

  // Sent on its own rather than through the profile save: every other field on this screen is
  // null in this payload, and the handler skips nulls, so nothing else is touched.
  const handleEnhancedInputToggle = useCallback(
    (next: boolean) => {
      if (!player?.id) return;
      setEnhancedInput(next);
      setEnhancedInputSaving(true);
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      const sent = socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId: generateId(),
        enhancedInput: next,
      });
      setEnhancedInputSaving(false);
      if (sent === 'dropped') {
        setEnhancedInput(!next);
        setError(t('profile.saveFailed'));
      }
    },
    [player?.id, t, user?.token]
  );

  // Applied locally straight away so every open screen reflows immediately, then persisted.
  // On failure the local value is put back, because a setting that silently disagrees with
  // the server is worse than one that visibly refused.
  const handleDistanceUnitChange = useCallback(
    (next: DistanceUnit) => {
      if (!player?.id || next === distanceUnit) return;
      const previous = distanceUnit;
      setDistanceUnit(next);
      setDistanceUnitSaving(true);
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      const sent = socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId: generateId(),
        distanceUnit: next,
      });
      setDistanceUnitSaving(false);
      if (sent === 'dropped') {
        setDistanceUnit(previous);
        setError(t('profile.saveFailed'));
      }
    },
    [distanceUnit, player?.id, setDistanceUnit, t, user?.token]
  );

  /**
   * There is no Save button: the fields in the first section commit themselves, the way every
   * toggle further down the screen already did. Text fields commit on blur, the chips, the date
   * picker and the home-club list from the effect below, and anything still pending is flushed on
   * the way out.
   */
  const commitProfile = useCallback(() => {
    if (!player?.id || loading) return;
    const decision = buildProfileUpdate(formFieldsRef.current, lastSentPayloadRef.current);
    if (decision.status === 'invalid') {
      setHoldReason(decision.messageKey);
      setAutosave('failed');
      return;
    }
    setHoldReason(null);
    if (decision.status === 'clean') {
      // Only a resolved hold gets here as 'failed' — a failed send clears the baseline, so it
      // comes back as 'ready' and re-sends instead.
      setAutosave((prev) => (prev === 'failed' ? 'idle' : prev));
      return;
    }
    // Recorded before the send, not on the ack: a second commit landing while the first is still
    // in flight would otherwise send the same values again.
    lastSentPayloadRef.current = serializeProfileUpdate(decision.payload);
    sendProfileUpdate(decision.payload);
  }, [loading, player?.id, sendProfileUpdate]);

  const commitRef = useRef(commitProfile);
  useEffect(() => {
    commitRef.current = commitProfile;
  });

  // Fold the loaded profile into the baseline once it has settled, so the first commit after a
  // load has nothing to say.
  useEffect(() => {
    if (loading || !player?.id || baselineSeededRef.current) return;
    baselineSeededRef.current = true;
    const decision = buildProfileUpdate(formFieldsRef.current, null);
    if (decision.status === 'ready') {
      lastSentPayloadRef.current = serializeProfileUpdate(decision.payload);
    }
  }, [loading, player?.id]);

  // The chips, the date picker and the home-club list have no blur to hang a commit on, so they
  // save from here — once, after the state they set has settled. Declared after the seeding effect
  // so a freshly loaded profile is already the baseline by the time this runs.
  useEffect(() => {
    commitRef.current();
  }, [gender, handicapDate, homeClubs, searchVisibility]);

  // A field edited and then left without blurring — Back, a stack pop, a logout — still saves.
  // The flush arms a fresh timeout, so it has to clear it again itself: the general cleanup above
  // is declared earlier and has already run by the time this one does.
  useEffect(
    () => () => {
      commitRef.current();
      clearSaveTimeout();
    },
    [clearSaveTimeout]
  );

  const parseDateInput = (value: string) => localDateToDate(value);
  const formatDateInput = (value: Date) => formatLocalDate(value);

  const handleRemoveAvatar = useCallback(() => {
    if (!player?.id || (!player.imageKey && !player.imageThumbKey)) return;
    setSaving(true);
    setError(null);
    setStatusNote(t('profile.saving'));
    const requestId = generateId();
    updateRequestIdRef.current = requestId;
    socketRef.current.setAuthToken(user?.token ?? null);
    // 'queued' is not a failure — the update is flushed when the socket (re)opens;
    // armSaveTimeout is the only thing that gives up.
    socketRef.current.send({
      type: 'player.profile.update',
      playerId: player.id,
      requestId,
      imageKey: null,
      removeAvatar: true,
    });
    armSaveTimeout(requestId);
    setAvatarRemovedLocally(true);
    setPlayer((prev) => (prev ? { ...prev, imageKey: null, imageThumbKey: null } : prev));
  }, [armSaveTimeout, player?.id, player?.imageKey, player?.imageThumbKey, t, user?.token]);

  const buildAvatarSvg = useCallback((seed: string) => createAvatarSvg(seed), []);

  const generateAvatarChoices = useCallback(() => {
    setAvatarChoices(() =>
      Array.from({ length: 12 }, () => ({
        id: generateId(),
        seed: generateId(),
      }))
    );
  }, []);

  useEffect(() => {
    if (!avatarPickerOpen) return;
    if (avatarRefreshLock.current) return;
    avatarRefreshLock.current = true;
    if (avatarChoices.length === 0) {
      generateAvatarChoices();
    }
    setTimeout(() => {
      avatarRefreshLock.current = false;
    }, 0);
  }, [avatarPickerOpen, generateAvatarChoices, avatarChoices.length]);

  const openAvatarPicker = useCallback(() => {
    generateAvatarChoices();
    setAvatarPickerOpen(true);
  }, [generateAvatarChoices]);

  // This flag only hides the tip for the current visit — a lasting opt-out is stored
  // per user by the coach mark service, so the tip returns on the next visit.
  useEffect(() => {
    setImageCoachMarkDismissed(false);
  }, [isFocused]);

  useEffect(() => {
    if (pathname === '/profile') {
      setImageCoachMarkDismissed(false);
    }
  }, [pathname]);

  const requestPresign = useCallback(
    (payload: PendingUpload) => {
      if (!player?.id) return;
      pendingUploadRef.current = payload;
      const requestId = generateId();
      imageRequestIdRef.current = requestId;
      setUploading(true);
      setStatusNote(t('profile.uploading'));
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.image.presign',
        playerId: player.id,
        requestId,
        fileName: payload.fileName,
        contentType: payload.contentType,
        fileSize: payload.fileSize,
      });
    },
    [player?.id, t, user?.token]
  );

  const selectAvatar = useCallback(
    (seed: string) => {
      if (!player?.id) return;
      const svg = buildAvatarSvg(seed);
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      requestPresign({
        blob,
        contentType: 'image/svg+xml',
        fileName: `player-${player.id}-${seed}.svg`,
        fileSize: blob.size,
      });
      setAvatarPickerOpen(false);
    },
    [buildAvatarSvg, player?.id, requestPresign]
  );

  const handleWebSelect = useCallback(
    (event: any) => {
      const file = event.target.files?.[0] as File | undefined;
      if (!file || !player?.id) return;
      requestPresign({
        blob: file,
        contentType: file.type || 'image/jpeg',
        fileName: file.name,
        fileSize: file.size,
      });
      event.target.value = '';
    },
    [player?.id, requestPresign]
  );

  const handleMobileSelect = useCallback(async () => {
    if (!player?.id) return;
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError(t('profile.permissionDenied'));
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.9,
    });
    if (result.canceled || result.assets.length === 0) return;
    const asset = result.assets[0];
    const response = await fetch(asset.uri);
    const blob = await response.blob();
    requestPresign({
      blob,
      contentType: blob.type || 'image/jpeg',
      fileName: asset.fileName ?? `player-${player.id}.jpg`,
      fileSize: blob.size,
    });
  }, [player?.id, requestPresign, t]);

  const uploadButtonPress = useCallback(() => {
    if (uploading) return;
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      void handleMobileSelect();
    }
  }, [handleMobileSelect, uploading]);

  const goBack = useCallback(() => {
    commitRef.current();
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/my-tours');
  }, [router]);

  const removeHomeClub = useCallback((clubId: string) => {
    setHomeClubs((prev) => {
      const remaining = prev.filter((club) => club.clubId !== clubId);
      if (remaining.length > 0 && !remaining.some((club) => club.isPrimary)) {
        return remaining.map((club, index) => ({ ...club, isPrimary: index === 0 }));
      }
      return remaining;
    });
  }, []);

  const setPrimaryHomeClub = useCallback((clubId: string) => {
    setHomeClubs((prev) => prev.map((club) => ({ ...club, isPrimary: club.clubId === clubId })));
  }, []);

  /**
   * react-native-web's modal focus trap hands focus back to whatever was focused when the modal
   * opened -- here, the search field whose onFocus opens it -- so without a guard the picker
   * reopens itself the moment it closes.
   *
   * The restore is NOT synchronous with the close: `animationType="fade"` keeps the modal's
   * children mounted through the 250ms fade-out, and the trap only restores in its unmount
   * cleanup. So the flag has to be cleared by the modal's `onDismiss`, which react-native-web
   * fires in the effect right after that unmount -- a timeout would have expired long before.
   *
   * Web only: native never restores focus, and `onDismiss` is iOS-only there, so arming this off
   * the web would leave the flag set forever and wedge the picker shut.
   */
  const suppressReopenRef = useRef(false);
  const clubSearchRef = useRef<TextInput | null>(null);
  const closeClubPicker = useCallback(() => {
    if (Platform.OS === 'web') {
      suppressReopenRef.current = true;
    }
    setClubPickerOpen(false);
  }, []);

  const addHomeClub = useCallback((club: ClubOption) => {
    setHomeClubs((prev) => {
      if (prev.some((existing) => existing.clubId === club.id)) {
        return prev;
      }
      const isPrimary = prev.length === 0;
      return [
        ...prev,
        {
          clubId: club.id,
          clubName: club.name,
          clubCity: club.city ?? null,
          isPrimary,
        },
      ];
    });
    setClubSearch('');
    closeClubPicker();
  }, [closeClubPicker]);

  /**
   * A club only exists once a course names it, so "add a club" is the course form. Coming back
   * refocuses the screen, which reloads the club list, so the new club is selectable straight away.
   */
  const openCreateClub = useCallback(() => {
    closeClubPicker();
    router.push('/config-course?closeOnSave=1');
  }, [closeClubPicker, router]);

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
      stickyHeaderIndices={[0]}
    >
      <View style={styles.stickyBar}>
        <Pressable style={styles.backButton} onPress={goBack}>
          <Text style={styles.backButtonText}>{t('menu.back')}</Text>
        </Pressable>
        <View style={styles.saveChip}>
          {autosaveLabel ? (
            <Text
              testID="profile-autosave-state"
              style={[styles.saveChipText, autosave === 'failed' && styles.saveChipTextFailed]}
            >
              {autosaveLabel}
            </Text>
          ) : null}
        </View>
      </View>
      <View style={styles.header}>
        <View style={styles.avatarWrap}>
          {resolvedImage ? (
            <Image source={{ uri: resolvedImage }} style={styles.avatar} resizeMode="cover" />
          ) : (
            <View style={styles.avatarPlaceholder} />
          )}
        </View>
        <View style={styles.headerContent}>
          <Text style={styles.title}>{t('profile.title')}</Text>
          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={appColors.accent.primary} />
              <Text style={styles.meta}>{t('profile.loading')}</Text>
            </View>
          ) : null}
          {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
          {error ? <Text style={styles.error}>{error}</Text> : null}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.displayNameLabel')}</Text>
        <GoffyInput
          value={displayName}
          onChangeText={setDisplayName}
          onBlur={commitProfile}
          editable={!loading}
          placeholder={t('profile.displayNamePlaceholder')}
        />
        <Text style={styles.label}>{t('profile.fullNameLabel')}</Text>
        <GoffyInput
          value={fullName}
          editable={false}
          placeholder={t('profile.fullNamePlaceholder')}
          style={styles.readonlyInput}
        />
        <Text style={styles.label}>{t('profile.emailLabel')}</Text>
        <GoffyInput
          value={email}
          onChangeText={setEmail}
          onBlur={commitProfile}
          editable={!loading}
          placeholder={t('profile.emailPlaceholder')}
          keyboardType="email-address"
          autoCapitalize="none"
        />
        <Text style={styles.label}>{t('profile.genderLabel')}</Text>
        <View style={styles.genderOptionsRow}>
          {genderOptions.map((option) => {
            const selected = gender === option.value;
            return (
              <Pressable
                key={option.value}
                style={[styles.genderOptionButton, selected && styles.genderOptionButtonSelected]}
                onPress={() => setGender(option.value)}
                disabled={loading}
              >
                <Text style={[styles.genderOptionText, selected && styles.genderOptionTextSelected]}>
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.inlineRow}>
          <View style={styles.inlineInputGroup}>
            <Text style={styles.label}>{t('profile.handicapLabel')}</Text>
            <GoffyInput
              value={handicap}
              onChangeText={setHandicap}
              onBlur={commitProfile}
              editable={!loading}
              placeholder={t('profile.handicapPlaceholder')}
              keyboardType="decimal-pad"
            />
          </View>
          <View style={styles.inlineInputGroup}>
            <Text style={styles.label}>{t('profile.handicapDateLabel')}</Text>
            <Pressable
              style={styles.input}
              onPress={() => setHandicapDatePickerOpen(true)}
              disabled={loading}
            >
              <Text style={handicapDate ? styles.inputText : styles.inputPlaceholderText}>
                {handicapDate || t('profile.handicapDatePlaceholder')}
              </Text>
            </Pressable>
          </View>
        </View>
        <Text style={styles.label}>{t('profile.golfIdLabel')}</Text>
        <GoffyInput
          value={golfId}
          onChangeText={setGolfId}
          onBlur={commitProfile}
          editable={!loading}
          placeholder={t('profile.golfIdPlaceholder')}
          autoCapitalize="characters"
          autoCorrect={false}
          maxLength={golfIdMaxLength}
        />
        <Text style={styles.meta}>{t('profile.golfIdHelp')}</Text>
        <Text style={styles.label}>{t('profile.searchVisibilityLabel')}</Text>
        <View style={styles.genderOptionsRow}>
          {searchVisibilityOptions.map((option) => {
            const selected = searchVisibility === option;
            return (
              <Pressable
                key={option}
                testID={`search-visibility-${option}`}
                style={[styles.genderOptionButton, selected && styles.genderOptionButtonSelected]}
                onPress={() => setSearchVisibility(option)}
                disabled={loading}
              >
                <Text style={[styles.genderOptionText, selected && styles.genderOptionTextSelected]}>
                  {t(searchVisibilityLabelKeys[option])}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <Text style={styles.meta}>{t(searchVisibilityHelpKeys[searchVisibility])}</Text>
        <Text style={styles.label}>{t('profile.homeClubsLabel')}</Text>
        {homeClubs.length === 0 ? (
          <Text style={styles.meta}>{t('profile.homeClubsEmpty')}</Text>
        ) : (
          <View style={styles.homeClubList}>
            {homeClubs.map((club) => (
              <View key={club.clubId} style={styles.homeClubItem}>
                <View style={styles.homeClubTextWrap}>
                  <Text style={styles.homeClubName}>{club.clubName}</Text>
                  {club.clubCity ? <Text style={styles.homeClubMeta}>{club.clubCity}</Text> : null}
                </View>
                <View style={styles.homeClubActions}>
                  <Pressable
                    style={[styles.homeClubActionButton, club.isPrimary && styles.homeClubActionButtonPrimary]}
                    onPress={() => setPrimaryHomeClub(club.clubId)}
                    disabled={loading}
                  >
                    <Text style={[styles.homeClubActionButtonText, club.isPrimary && styles.homeClubActionButtonTextPrimary]}>
                      {club.isPrimary ? t('profile.homeClubsPrimary') : t('profile.homeClubsSetPrimary')}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={styles.homeClubRemoveButton}
                    onPress={() => removeHomeClub(club.clubId)}
                    disabled={loading}
                  >
                    <Text style={styles.homeClubRemoveText}>{t('profile.homeClubsRemove')}</Text>
                  </Pressable>
                </View>
              </View>
            ))}
          </View>
        )}
        <GoffyInput
          testID="home-club-search"
          ref={clubSearchRef}
          value={clubSearch}
          onChangeText={(value) => {
            setClubSearch(value);
            setClubPickerOpen(true);
          }}
          onFocus={() => {
            if (suppressReopenRef.current) {
              // Swallowing the event is not enough: the field would stay focused, so the user's
              // next tap raises no new focus event and the picker would never open again.
              clubSearchRef.current?.blur();
              return;
            }
            setClubPickerOpen(true);
          }}
          placeholder={t('profile.homeClubsAdd')}
          editable={!loading && !saving}
          autoCorrect={false}
          style={styles.homeClubSearchField}
        />
        {holdReason ? (
          <Text testID="profile-save-hint" style={styles.saveHint}>
            {t(holdReason)}
          </Text>
        ) : null}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.imageLabel')}</Text>
        <View style={styles.buttonRow} ref={imageActionsAnchorRef}>
          <GoffyButton
            label={t('profile.uploadImage')}
            onPress={uploadButtonPress}
            disabled={uploading}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
          <GoffyButton
            label={t('profile.selectAvatar')}
            onPress={openAvatarPicker}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
          <GoffyButton
            label={t('profile.removeAvatar')}
            onPress={handleRemoveAvatar}
            disabled={saving || uploading || !resolvedImage}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.enhancedInputSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.enhancedInputHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.enhancedInputToggleLabel')}</Text>
          <Switch
            testID="enhanced-input-toggle"
            value={enhancedInput}
            disabled={loading || enhancedInputSaving || !player?.id}
            onValueChange={handleEnhancedInputToggle}
            accessibilityLabel={t('profile.enhancedInputToggleLabel')}
            trackColor={{ false: appColors.surface.toggleOff, true: appColors.accent.primary }}
          />
          <Text style={styles.meta}>
            {enhancedInput ? t('profile.enhancedInputOn') : t('profile.enhancedInputOff')}
          </Text>
        </View>
      </View>

      {/* Presentation only — every distance stays stored in metres, so switching this
          changes what is displayed and nothing that is saved. */}
      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.distanceUnitSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.distanceUnitHint')}</Text>
        <View style={styles.unitRow}>
          {(['metric', 'imperial'] as const).map((option) => (
            <Pressable
              key={option}
              testID={`distance-unit-${option}`}
              accessibilityRole="radio"
              aria-checked={distanceUnit === option}
              disabled={loading || distanceUnitSaving || !player?.id}
              style={[styles.unitChip, distanceUnit === option && styles.unitChipActive]}
              onPress={() => handleDistanceUnitChange(option)}
            >
              <Text style={styles.unitChipText}>
                {t(option === 'metric' ? 'profile.distanceUnitMetric' : 'profile.distanceUnitImperial')}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      {isAdmin ? (
        <View style={styles.section} testID="skin-section">
          <Text style={styles.label}>{t('profile.skinSectionLabel')}</Text>
          <Text style={styles.meta}>{t('profile.skinHint')}</Text>
          <SkinPicker />
        </View>
      ) : null}

      {/* Per-device consent, deliberately NOT a profile flag: switching sensors on must
          never follow an account to a new phone. See stores/shotTrackingState. */}
      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.shotTrackingSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.shotTrackingHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.shotTrackingToggleLabel')}</Text>
          <Switch
            testID="shot-tracking-toggle"
            value={shotTrackingEnabled}
            onValueChange={setShotTrackingEnabled}
            accessibilityLabel={t('profile.shotTrackingToggleLabel')}
            trackColor={{ false: appColors.surface.toggleOff, true: appColors.accent.primary }}
          />
          <Text style={styles.meta}>
            {shotTrackingEnabled ? t('profile.shotTrackingOn') : t('profile.shotTrackingOff')}
          </Text>
        </View>
        {Platform.OS !== 'android' ? (
          <Text style={styles.meta}>{t('profile.shotTrackingAndroidOnly')}</Text>
        ) : null}
        {isDeveloper ? (
          <GoffyButton
            label={t('profile.shotTrackingDebugLink')}
            onPress={() => router.push('/shot-tracking-debug')}
            tone="dark"
            testID="shot-tracking-debug-link"
          />
        ) : null}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.showDistanceSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.showDistanceHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.showDistanceToggleLabel')}</Text>
          <Switch
            testID="show-distance-toggle"
            value={showDistanceEnabled}
            onValueChange={setShowDistanceEnabled}
            accessibilityLabel={t('profile.showDistanceToggleLabel')}
            trackColor={{ false: appColors.surface.toggleOff, true: appColors.accent.primary }}
          />
          <Text style={styles.meta}>
            {showDistanceEnabled ? t('profile.showDistanceOn') : t('profile.showDistanceOff')}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.hazardAnimationsSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.hazardAnimationsHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.hazardAnimationsToggleLabel')}</Text>
          <Switch
            testID="hazard-animations-toggle"
            value={hazardAnimationsEnabled}
            onValueChange={setHazardAnimationsEnabled}
            accessibilityLabel={t('profile.hazardAnimationsToggleLabel')}
            trackColor={{ false: appColors.surface.toggleOff, true: appColors.accent.primary }}
          />
          <Text style={styles.meta}>
            {hazardAnimationsEnabled ? t('profile.hazardAnimationsOn') : t('profile.hazardAnimationsOff')}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.coachTipsSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.coachTipsHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.coachTipsToggleLabel')}</Text>
          <Switch
            testID="coach-tips-toggle"
            value={coachTipsEnabled}
            disabled={coachTipsLoading || coachTipsSaving}
            onValueChange={(next) => {
              setCoachTipsError(null);
              void setCoachTipsEnabled(next).catch(() => {
                setCoachTipsError(t('profile.coachTipsSaveFailed'));
              });
            }}
            accessibilityLabel={t('profile.coachTipsToggleLabel')}
            trackColor={{ false: appColors.surface.toggleOff, true: appColors.accent.primary }}
          />
          <Text style={styles.meta}>
            {coachTipsEnabled ? t('profile.coachTipsOn') : t('profile.coachTipsOff')}
          </Text>
        </View>
        {hasDismissedTips || !coachTipsEnabled ? (
          <View style={styles.buttonRow}>
            <GoffyButton
              label={coachTipsSaving ? t('profile.coachTipsResetting') : t('profile.coachTipsReset')}
              onPress={() => {
                setCoachTipsError(null);
                void resetCoachTips().catch(() => {
                  setCoachTipsError(t('profile.coachTipsSaveFailed'));
                });
              }}
              disabled={coachTipsSaving}
              tone="dark"
              testID="coach-tips-reset"
              oldButtonStyle={styles.secondaryButton}
              oldTextStyle={styles.secondaryButtonText}
              oldDisabledStyle={styles.primaryButtonDisabled}
              style={styles.secondaryButtonImageMode}
              textStyle={styles.secondaryButtonImageText}
            />
          </View>
        ) : null}
        {coachTipsError ? <Text style={styles.error}>{coachTipsError}</Text> : null}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.passwordSectionLabel')}</Text>
        {hasPassword === false ? <Text style={styles.meta}>{t('profile.passwordSetHint')}</Text> : null}
        <View style={styles.buttonRow}>
          <GoffyButton
            label={hasPassword === false ? t('profile.setPassword') : t('profile.changePassword')}
            onPress={() => {
              resetPasswordState();
              setPasswordModalOpen(true);
            }}
            disabled={loading}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
        </View>
      </View>

      <ApiAccessSection
        apiBaseUrl={apiBaseUrl}
        token={user?.token}
        disabled={loading}
        onDialogOpenChange={setApiAccessDialogOpen}
      />

      <View style={[styles.section, styles.dangerSection]}>
        <Text style={styles.label}>{t('profile.deleteAccountSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.deleteAccountHint')}</Text>
        <View style={styles.buttonRow}>
          <Pressable
            style={[styles.dangerButton, (loading || deletingAccount) && styles.primaryButtonDisabled]}
            onPress={() => {
              resetDeleteAccountState();
              setDeleteAccountModalOpen(true);
            }}
            disabled={loading || deletingAccount}
            testID="delete-account-open"
            accessibilityLabel={t('profile.deleteAccount')}
          >
            <Text style={styles.dangerButtonText}>{t('profile.deleteAccount')}</Text>
          </Pressable>
        </View>
      </View>

      {Platform.OS === 'web' ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleWebSelect}
          style={{ display: 'none' }}
        />
      ) : null}
      <Modal
        testID="home-club-picker"
        transparent
        visible={clubPickerOpen}
        animationType="fade"
        onRequestClose={closeClubPicker}
        onDismiss={() => {
          suppressReopenRef.current = false;
        }}
      >
        <Pressable style={styles.modalBackdrop} onPress={closeClubPicker}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.homeClubsPickerTitle')}</Text>
            <GoffyInput
              value={clubSearch}
              onChangeText={setClubSearch}
              placeholder={t('profile.homeClubsSearchPlaceholder')}
              autoCorrect={false}
              autoFocus
              style={styles.clubPickerSearchInput}
            />
            <ScrollView style={styles.homeClubPickerList} contentContainerStyle={styles.homeClubPickerListContent}>
              {loadingClubOptions ? <Text style={styles.avatarLoading}>{t('profile.homeClubsLoading')}</Text> : null}
              {!loadingClubOptions && filteredClubOptions.length === 0 ? (
                <View style={styles.clubPickerEmpty}>
                  <Text style={styles.avatarLoading}>{t('profile.homeClubsNoResults')}</Text>
                  <Pressable
                    testID="home-club-create"
                    style={styles.addHomeClubButton}
                    onPress={openCreateClub}
                    accessibilityLabel={t('profile.homeClubsCreate')}
                  >
                    <Text style={styles.addHomeClubButtonText}>{t('profile.homeClubsCreate')}</Text>
                  </Pressable>
                </View>
              ) : null}
              {!loadingClubOptions
                ? filteredClubOptions.map((club) => (
                    <Pressable
                      key={club.id}
                      testID={`club-option-${club.id}`}
                      style={styles.clubOptionRow}
                      onPress={() => addHomeClub(club)}
                    >
                      <Text style={styles.clubOptionName}>{club.name}</Text>
                      {(club.courses ?? []).length > 0 ? (
                        <Text style={styles.clubOptionCourses}>
                          {(club.courses ?? []).map((course) => course.name).join(' · ')}
                        </Text>
                      ) : null}
                      <Text style={styles.clubOptionMeta}>
                        {[club.city, club.country].filter(Boolean).join(', ') || t('profile.homeClubsUnknownLocation')}
                      </Text>
                    </Pressable>
                  ))
                : null}
            </ScrollView>
            <View style={styles.avatarModalActions}>
              <Pressable
                style={[styles.moreButton, loadingClubOptions && styles.primaryButtonDisabled]}
                onPress={() => {
                  void loadClubOptions();
                }}
                disabled={loadingClubOptions}
              >
                <Text style={styles.moreButtonText}>{t('profile.homeClubsRefresh')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={closeClubPicker}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
      <ChangePasswordModal
        visible={passwordModalOpen}
        requireCurrent={hasPassword !== false}
        title={hasPassword === false ? t('profile.setPassword') : t('profile.changePassword')}
        pending={passwordSaving}
        errorText={passwordError}
        onSubmit={changePassword}
        onClose={() => setPasswordModalOpen(false)}
      />
      <DeleteAccountModal
        visible={deleteAccountModalOpen}
        pending={deletingAccount}
        errorText={deleteAccountError}
        onConfirm={() => {
          void deleteAccount();
        }}
        onClose={() => {
          if (deletingAccount) return;
          setDeleteAccountModalOpen(false);
        }}
      />
      <Modal transparent visible={avatarPickerOpen} animationType="fade" onRequestClose={() => setAvatarPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setAvatarPickerOpen(false)}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.avatarPickerTitle')}</Text>
            <View style={styles.avatarGrid}>
              {avatarChoices.length === 0 ? (
                <Text style={styles.avatarLoading}>{t('profile.avatarLoading')}</Text>
              ) : (
                avatarChoices.map(({ id, seed }) => {
                  const svg = buildAvatarSvg(seed);
                  return (
                    <Pressable key={id} testID="avatar-option" style={styles.avatarChoice} onPress={() => selectAvatar(seed)}>
                      <SvgXml xml={svg} width="100%" height="100%" />
                    </Pressable>
                  );
                })
              )}
            </View>
            <View style={styles.avatarModalActions}>
              <Pressable style={styles.moreButton} onPress={generateAvatarChoices}>
                <Text style={styles.moreButtonText}>{t('profile.avatarMore')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={() => setAvatarPickerOpen(false)}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
      <PlatformDatePicker
        open={handicapDatePickerOpen}
        date={parseDateInput(handicapDate) ?? new Date()}
        onConfirm={(date) => {
          setHandicapDate(formatDateInput(date));
          setHandicapDatePickerOpen(false);
        }}
        onCancel={() => setHandicapDatePickerOpen(false)}
      />
      <CoachMarkCallout
        visible={showImageCoachMark}
        targetRef={imageActionsAnchorRef}
        title={t('coachMarks.profileImageActionsTitle')}
        description={t('coachMarks.profileImageActionsBody')}
        actionLabel={t('coachMarks.gotIt')}
        onDismiss={() => setImageCoachMarkDismissed(true)}
        hideForeverLabel={t('coachMarks.hideForever')}
        disableAllLabel={t('coachMarks.disableAll')}
        onDismissForever={() => {
          setImageCoachMarkDismissed(true);
          void dismissImageCoachMarkForever().catch(() => {
            setImageCoachMarkDismissed(false);
          });
        }}
        onDisableAll={() => {
          setImageCoachMarkDismissed(true);
          void disableAllCoachMarks().catch(() => {
            setImageCoachMarkDismissed(false);
          });
        }}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.panel,
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  // Pinned via the ScrollView's stickyHeaderIndices: with the section's Save button gone, Back and
  // the save state are the two things that have to stay on screen at any scroll position. The
  // negative margin bleeds the opaque background past the container padding, so content does not
  // show through the gutters as it scrolls underneath.
  stickyBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    backgroundColor: appColors.background.panel,
    marginHorizontal: -16,
    paddingHorizontal: 16,
    paddingVertical: 8,
    // Without an edge, content sliding underneath reads as clipped rather than scrolled.
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.strong,
  },
  saveChip: {
    height: 34,
    justifyContent: 'center',
  },
  saveChipText: {
    color: appColors.accent.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  saveChipTextFailed: {
    color: appColors.accent.gold,
  },
  saveHint: {
    color: appColors.accent.gold,
    fontSize: 12,
  },
  // A pale fill carrying near-white text read as a smudge rather than a control: #C8C8B0 under
  // #F5F5DC is about 1.2:1. Dark pill, accent text and border — the shape the back buttons on the
  // other screens already use, and legible against the sticky bar behind it.
  backButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.surface.elevated,
    paddingHorizontal: 14,
    height: 36,
    justifyContent: 'center',
  },
  backButtonText: {
    color: appColors.accent.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  avatarWrap: {
    width: 88,
    height: 88,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    flex: 1,
    backgroundColor: appColors.text.secondary,
  },
  headerContent: {
    flex: 1,
    gap: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meta: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  error: {
    color: appColors.status.dangerText,
    fontSize: 12,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  unitRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  unitChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  unitChipActive: {
    borderColor: appColors.border.accent,
    borderWidth: 2,
  },
  unitChipText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  coachTipsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  coachTipsLabel: {
    color: appColors.text.primary,
    fontSize: 14,
    flexShrink: 1,
  },
  section: {
    gap: 10,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  dangerSection: {
    borderColor: appColors.status.dangerBg,
  },
  dangerButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.status.dangerBg,
    backgroundColor: appColors.status.dangerPanel,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  dangerButtonText: {
    color: appColors.status.dangerTextSoft,
    fontWeight: '700',
    fontSize: 14,
  },
  inlineRow: {
    flexDirection: 'row',
    gap: 10,
  },
  inlineInputGroup: {
    flex: 1,
    gap: 6,
  },
  genderOptionsRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  genderOptionButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  genderOptionButtonSelected: {
    borderColor: appColors.border.accent,
    backgroundColor: appColors.border.strong,
  },
  genderOptionText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  genderOptionTextSelected: {
    color: appColors.text.primary,
  },
  label: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  // Only the handicap-date field still uses this: it is a Pressable dressed as a field, so it
  // cannot be a GoffyInput. Kept in step with GoffyInput's frame so it matches the real inputs
  // beside it — the old '#07200D' fill was identical to the section behind it.
  input: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    fontSize: 14,
    backgroundColor: appColors.surface.input,
  },
  readonlyInput: {
    opacity: 0.8,
  },
  inputText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  // Matches GoffyInput's placeholder tone, so the date field reads like the fields around it.
  inputPlaceholderText: {
    color: appColors.text.muted,
    fontSize: 14,
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  homeClubList: {
    gap: 8,
  },
  homeClubItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accentMuted,
    backgroundColor: appColors.surface.muted,
    padding: 10,
    gap: 8,
  },
  homeClubTextWrap: {
    gap: 2,
  },
  homeClubName: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  homeClubMeta: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  homeClubActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  homeClubActionButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.default,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: appColors.background.panel,
  },
  // Marks the club that IS primary, so it has to look distinct. The fill was
  // appColors.border.default — a dark green at 15% opacity over '#07200D' — which composited to
  // within a couple of shades of the unselected state, making the two indistinguishable.
  homeClubActionButtonPrimary: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  homeClubActionButtonText: {
    color: appColors.text.secondary,
    fontWeight: '700',
    fontSize: 12,
  },
  homeClubActionButtonTextPrimary: {
    color: appColors.text.inverse,
  },
  homeClubRemoveButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.status.dangerBg,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: appColors.status.dangerPanel,
  },
  homeClubRemoveText: {
    color: appColors.status.dangerTextSoft,
    fontWeight: '700',
    fontSize: 12,
  },
  addHomeClubButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accentMuted,
    backgroundColor: appColors.surface.muted,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  addHomeClubButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  homeClubSearchField: {
    marginTop: 4,
  },
  clubPickerSearchInput: {
    marginTop: 10,
  },
  clubPickerEmpty: {
    gap: 10,
    alignItems: 'flex-start',
  },
  secondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  secondaryButtonImageMode: {
    width: 190,
    height: 56,
  },
  secondaryButtonImageText: {
    fontSize: 16,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.18),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  avatarModalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  avatarModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
    marginBottom: 12,
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  homeClubPickerList: {
    maxHeight: 320,
    marginTop: 10,
  },
  homeClubPickerListContent: {
    gap: 8,
    paddingBottom: 8,
  },
  clubOptionRow: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accentMuted,
    backgroundColor: appColors.surface.muted,
    paddingHorizontal: 10,
    paddingVertical: 10,
    gap: 2,
  },
  clubOptionName: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  clubOptionCourses: {
    color: appColors.text.primary,
    fontSize: 13,
  },
  clubOptionMeta: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  avatarLoading: {
    color: appColors.text.primary,
    fontSize: 12,
  },
  avatarTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  avatarTab: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  avatarTabActive: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.border.accent,
  },
  avatarTabText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 12,
  },
  avatarChoice: {
    width: 64,
    height: 64,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    overflow: 'hidden',
    backgroundColor: appColors.background.panelAlt,
  },
  avatarChoiceImage: {
    width: '100%',
    height: '100%',
  },
  avatarChoiceSvg: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  moreButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  moreButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  closeButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: appColors.background.interactive,
  },
  closeButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
});

/** Maps the server's error codes onto translated copy, falling back to a generic save failure. */
function translateUpdateError(code: string | undefined, t: (key: string) => string): string {
  switch (code) {
    case 'user.golfId.invalid':
      return t('profile.invalidGolfId');
    case 'user.golfId.exists':
      return t('profile.golfIdTaken');
    case 'user.searchVisibility.golfIdRequired':
      return t('profile.searchVisibilityGolfIdRequired');
    default:
      return t('profile.saveFailed');
  }
}

// UUID-shaped even without crypto.randomUUID (Hermes has none): the BFF deserializes requestId as
// a Guid, and the old `req_<time>_<random>` fallback meant player.image.presign was dropped
// unanswered on the native apps.
function generateId(): string {
  return createRequestId();
}
